import { useSyncExternalStore } from 'react';
import { getWebApp, headerFollowsTheme, reapplyBrandPrimary } from '@/lib/telegram';

/**
 * Тема и схема мини-аппа клиента.
 *
 * Тему выбирает партнёр (`miniapp_theme` в ответе входа), схему клиент своей темой
 * Telegram. Обе ложатся атрибутами на <html>, а значения цветов, радиусов и шрифтов
 * берёт index.css. Тема кэшируется по боту, как цвет бренда: иначе первый кадр
 * рисовался бы темой по умолчанию и через полсекунды перескакивал.
 */
export const THEMES = ['graphite', 'schema', 'cloud', 'classic'] as const;
/** «Классика» (вид до 19.10.2026) всегда тёмная: светлой у прежнего вида не было. */
const DARK_ONLY: readonly MiniappTheme[] = ['classic'];
export type MiniappTheme = (typeof THEMES)[number];
export const DEFAULT_THEME: MiniappTheme = 'graphite';

const isTheme = (v: unknown): v is MiniappTheme => typeof v === 'string' && (THEMES as readonly string[]).includes(v);
const themeKey = (botId: string) => `miniapp_theme:${botId}`;

/** Только для стенда в dev: `?theme=schema&scheme=light`, чтобы снять экраны без бэка. */
const devOverride = (name: 'theme' | 'scheme'): string | null => {
  if (!import.meta.env.DEV) return null;
  try {
    return new URLSearchParams(location.search).get(name);
  } catch {
    return null;
  }
};

export function applyTheme(theme: unknown, botId?: string | null): void {
  const forced = devOverride('theme');
  const value = isTheme(forced) ? forced : isTheme(theme) ? theme : DEFAULT_THEME;
  const prev = document.documentElement.dataset.theme;
  document.documentElement.dataset.theme = value;
  // Схема зависит от темы («Классика» всегда тёмная): пересчитать её при смене.
  if (prev !== value && (DARK_ONLY.includes(value) || DARK_ONLY.includes(prev as MiniappTheme))) applyScheme();
  if (!botId) return;
  try {
    if (isTheme(theme) && theme !== DEFAULT_THEME) localStorage.setItem(themeKey(botId), theme);
    else localStorage.removeItem(themeKey(botId));
  } catch {
    // приватный режим: просто без кэша
  }
}

/** До входа: тема из кэша этого бота. */
export function applyCachedTheme(botId: string | null): void {
  let cached: string | null = null;
  try {
    cached = botId ? localStorage.getItem(themeKey(botId)) : null;
  } catch {
    // ignore
  }
  applyTheme(cached);
}

const toHex = (rgb: string): string | null => {
  const m = rgb.match(/\d+(\.\d+)?/g);
  if (!m || m.length < 3) return null;
  return `#${m.slice(0, 3).map((n) => Math.round(Number(n)).toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Схема по Telegram, вне Telegram по системе. Фон вебвью и полоса под MainButton
 * красятся в фон темы (design-8): иначе при оттягивании страницы и под кнопкой
 * оплаты торчал цвет темы Telegram. Шапка тоже, если у бота нет своего цвета:
 * у «Схемы» и «Облака» фон не совпадает с фоном Telegram, и над страницей висела
 * полоса другого тона. Цвет бренда и header_color партнёра шапку не отдают. Цвет читаем у body, а не из переменной:
 * так он всегда совпадает с тем, что нарисовано.
 */
export function applyScheme(): void {
  const tg = getWebApp();
  const forced = devOverride('scheme');
  const darkOnly = DARK_ONLY.includes(document.documentElement.dataset.theme as MiniappTheme);
  const scheme = darkOnly
    ? 'dark'
    : forced === 'light' || forced === 'dark'
      ? forced
      : tg?.colorScheme ?? (window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark');
  document.documentElement.dataset.scheme = scheme;
  reapplyBrandPrimary();
  syncNativeBackground();
}

export function syncNativeBackground(): void {
  const tg = getWebApp();
  if (!tg) return;
  const hex = toHex(getComputedStyle(document.body).backgroundColor);
  if (!hex) return;
  try {
    if (tg.isVersionAtLeast?.('6.1')) {
      tg.setBackgroundColor(hex);
      if (headerFollowsTheme()) tg.setHeaderColor(hex);
    }
    if (tg.isVersionAtLeast?.('7.10')) tg.setBottomBarColor?.(hex);
  } catch {
    // старые клиенты: останется цвет темы Telegram
  }
}

export const currentScheme = (): 'light' | 'dark' =>
  document.documentElement.dataset.scheme === 'light' ? 'light' : 'dark';

const subscribeScheme = (cb: () => void) => {
  const mo = new MutationObserver(cb);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-scheme'] });
  return () => mo.disconnect();
};

/** Схема для компонентов, которым нужен цвет в JS (тосты). */
export const useScheme = () => useSyncExternalStore(subscribeScheme, currentScheme, () => 'dark' as const);
